import { useEffect, useState } from 'react'
import { classifyNetworkError, HEALTH_ID, reportHealthError } from '../analytics/appHealthEvents'
import { PrecomputedMmtgProvider } from '../geometry/PrecomputedMmtgProvider'
import type { GeometrySet } from '../geometry/types'
import { getMapBundleSession } from '../mapDelivery/defaultMapBundleSession'

/**
 * The late-night frame of the page's bundle (TASK-1136):
 * - `unknown` — not asked yet (night mode never entered) or being asked;
 * - `absent` — the bundle has none (London) or it failed to load: night mode
 *   is the day geometry filtered by `modes`, as before;
 * - `loading` — the bundle has one and its geometry is on the way;
 * - `ready` — `geometry` is the frame to draw in night mode.
 */
export type NightFrameStatus = 'unknown' | 'absent' | 'loading' | 'ready'

export interface NightFrame {
  readonly status: NightFrameStatus
  readonly geometry: GeometrySet | null
}

export interface NightFrameSource {
  hasNightFrame(): Promise<boolean>
  load(): Promise<GeometrySet>
}

const bundleSource: NightFrameSource = {
  hasNightFrame: () => getMapBundleSession().hasNightFrame(),
  load: () => new PrecomputedMmtgProvider().load('night'),
}

/**
 * Loads the late-night frame the first time night mode is `wanted` — a page
 * that never enters night mode never downloads it — and keeps it for the
 * page's life. A frame that fails to load is reported and left `absent`, so
 * the map falls back to the filtered day geometry instead of going blank.
 */
export function useNightFrameGeometry(
  wanted: boolean,
  source: NightFrameSource = bundleSource,
): NightFrame {
  const [frame, setFrame] = useState<NightFrame>({ status: 'unknown', geometry: null })
  const asked = wanted || frame.status !== 'unknown'

  useEffect(() => {
    if (!asked) return
    let disposed = false
    void (async () => {
      if (!(await source.hasNightFrame())) {
        if (!disposed) setFrame({ status: 'absent', geometry: null })
        return
      }
      if (disposed) return
      setFrame({ status: 'loading', geometry: null })
      try {
        const geometry = await source.load()
        if (!disposed) setFrame({ status: 'ready', geometry })
      } catch (err: unknown) {
        const { errorKind, statusCode } = classifyNetworkError(err)
        reportHealthError(HEALTH_ID.mmtgLoadFailed, `mmtg load (night) failed: ${String(err)}`, {
          domain: 'geometry',
          severity: 'warning',
          errorKind,
          statusCode,
        })
        if (!disposed) setFrame({ status: 'absent', geometry: null })
      }
    })()
    return () => {
      disposed = true
    }
    // Asked once: the status leaving `unknown` keeps `asked` true without re-running.
  }, [asked, source])

  return frame
}
